import { EmptyState, MaskedValue } from '@/components';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkSchedule } from '../../types';
import { parseWorkSchedule, type ParsedWorkSchedule } from './workScheduleParser';
import { WorkScheduleWeekView } from './WorkScheduleWeekView';

export interface WorkScheduleCardProps {
  workSchedule: WorkSchedule;
  // 4-digit code that overrules the schedule for one shift; undefined when unset.
  overrideKey?: string;
}

// WorkScheduleCard shows the read-only weekly schedules of the device and, below them, the
// masked overrule code. The schedule blob is parsed defensively; unreadable parts are flagged.
export function WorkScheduleCard({ workSchedule, overrideKey }: WorkScheduleCardProps) {
  const { t } = useTranslation();
  const parsed = useMemo(() => parseWorkSchedule(workSchedule.schedule), [workSchedule.schedule]);
  const scheduleCount = parsed.schedules.length;

  return (
    <PageContentCard className="w-full">
      {/* Title, summary and read-only badge */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-onsurface text-lg font-semibold">{t('devices.detail.rules.workSchedule.title')}</h2>
          <p className="text-muted text-sm">{t('devices.detail.rules.workSchedule.summary', { count: scheduleCount })}</p>
        </div>
        <span className="border-border text-muted rounded-md border px-3 py-1 text-xs whitespace-nowrap">
          {t('devices.detail.rules.workSchedule.readOnly')}
        </span>
      </div>

      <ScheduleContent parsed={parsed} />

      {/* Overrule code */}
      <div className="border-border mt-6 flex flex-wrap items-end gap-8 border-t pt-5 justify-center">
        <div>
          <div className="text-muted mb-2 text-xs">{t('devices.detail.rules.overrideKey.label')}</div>
          <MaskedValue value={overrideKey} />
        </div>
      </div>
    </PageContentCard>
  );
}

// ScheduleContent picks between the week view, the empty state and the unreadable notice.
function ScheduleContent({ parsed }: { parsed: ParsedWorkSchedule }) {
  const { t } = useTranslation();
  const hasSchedules = parsed.schedules.length > 0;

  return (
    <>
      {hasSchedules && <WorkScheduleWeekView schedules={parsed.schedules} />}
      {!hasSchedules && !parsed.hasUnrecognizedParts && (
        <EmptyState title={t('devices.detail.rules.workSchedule.empty')} className="py-8" />
      )}
      {parsed.hasUnrecognizedParts && (
        <p className="text-warning mt-3 text-sm">{t('devices.detail.rules.workSchedule.unrecognized')}</p>
      )}
    </>
  );
}
